import React, { useState } from 'react'
import { Building2, Package, Scale, AlertTriangle, History } from 'lucide-react'
import { toast } from 'sonner'
import { fmtEur } from './TransportSinistreBlocks'

const API = process.env.REACT_APP_BACKEND_URL

export const QUALIF_LABELS = {
  rc_contractuelle: { label: 'RC contractuelle', color: 'bg-amber-100 text-amber-800' },
  ad_valorem: { label: 'Ad valorem', color: 'bg-sky-100 text-sky-800' },
  hors_garantie: { label: 'Hors garantie', color: 'bg-slate-200 text-slate-700' },
}

const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR') : '--'

export default function TransportQualificationBlock({ sinistre, sinistreId, data, onRefresh, userId }) {
  const [open, setOpen] = useState(false)
  const [form, setForm] = useState({ qualification: sinistre.qualification || 'ad_valorem', motif: '', entite_id: sinistre.entite_id || '', garantie_id: sinistre.garantie_id || '' })
  const [saving, setSaving] = useState(false)
  const q = QUALIF_LABELS[sinistre.qualification]
  const derniere = data?.derniere_qualification
  const entites = (data?.entites || []).filter(e => e.actif !== false)
  const calc = sinistre.franchise_calcul

  async function submit(e) {
    e.preventDefault()
    if (!form.motif.trim()) { toast.error('Le motif est obligatoire'); return }
    setSaving(true)
    try {
      const res = await fetch(`${API}/api/sinistres/${sinistreId}/qualifier`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ...form, entite_id: form.entite_id || null, garantie_id: form.garantie_id || null, user_id: userId }),
      })
      const d = await res.json()
      if (!res.ok) { toast.error(d.detail || 'Erreur'); return }
      toast.success(`Qualification enregistree — police rattachee (${d.rattachement?.regle})`)
      if (d.rattachement?.alerte) toast.warning(d.rattachement.alerte)
      setOpen(false); setForm(f => ({ ...f, motif: '' }))
      onRefresh?.()
    } catch { toast.error('Erreur reseau') } finally { setSaving(false) }
  }

  return (
    <div className="bg-white rounded-xl border border-slate-200 shadow-sm overflow-hidden" data-testid="transport-qualification-block">
      <div className="px-5 py-3 border-b border-slate-100 bg-slate-50 flex items-center justify-between">
        <h2 className="font-semibold text-slate-800 text-sm flex items-center gap-2"><Scale size={14} className="text-sky-600" /> Entite & qualification</h2>
        <button onClick={() => setOpen(o => !o)} className="text-xs px-3 py-1.5 rounded-lg bg-sky-600 text-white hover:bg-sky-700" data-testid="btn-requalifier">
          {sinistre.qualification ? 'Requalifier' : 'Qualifier'}
        </button>
      </div>
      <div className="p-5 grid grid-cols-2 gap-x-8 gap-y-3 text-sm">
        <div>
          <p className="text-xs text-slate-400 flex items-center gap-1"><Building2 size={12} /> Entite</p>
          <p className="text-slate-800" data-testid="transport-entite">{data?.entite_chemin || sinistre.entites?.nom || <span className="text-red-500">Non rattache</span>}</p>
        </div>
        <div>
          <p className="text-xs text-slate-400">Qualification</p>
          {q ? <span className={`inline-block px-2 py-0.5 rounded text-xs font-medium ${q.color}`} data-testid="transport-qualification">{q.label}</span>
             : <span className="text-red-500 text-xs">A qualifier</span>}
          {sinistre.exclu_sp && <span className="ml-2 text-[11px] text-slate-500">exclu du S/P</span>}
        </div>
        <div>
          <p className="text-xs text-slate-400">Police rattachee (resultante)</p>
          <p className="text-slate-800 font-medium" data-testid="transport-police">{sinistre.polices?.numero_police || '--'} <span className="text-slate-500 font-normal">{sinistre.polices?.libelle}</span></p>
        </div>
        <div>
          <p className="text-xs text-slate-400 flex items-center gap-1"><Package size={12} /> Expedition</p>
          <p className="text-slate-800">{data?.expedition ? `${data.expedition.numero_ordre} — ${fmtDate(data.expedition.date_expedition)} ${data.expedition.mode || ''}` : '--'}</p>
        </div>
        <div className="col-span-2 bg-slate-50 rounded-lg px-3 py-2">
          <p className="text-xs text-slate-400 flex items-center gap-1"><History size={12} /> Dernier motif journalise</p>
          {derniere ? (
            <p className="text-slate-700 text-xs mt-0.5" data-testid="transport-dernier-motif">
              <span className="font-medium">{fmtDate(derniere.created_at)}</span>{derniere.user_profiles ? ` · ${derniere.user_profiles.prenom} ${derniere.user_profiles.nom}` : ''} — {derniere.contenu || derniere.titre}
              {derniere.nouvelle_valeur?.entites_parcourues && <span className="text-slate-400"> (parcours : {derniere.nouvelle_valeur.entites_parcourues.join(' → ')})</span>}
            </p>
          ) : <p className="text-slate-400 text-xs">{sinistre.qualification_motif || 'Aucune qualification journalisee'}</p>}
        </div>
        {calc && (
          <div className="col-span-2 text-xs text-slate-600 flex flex-wrap gap-x-4 gap-y-1" data-testid="transport-franchise-detail">
            <span>Indemnite brute <b>{fmtEur(calc.base?.indemnite_brute)}</b>{calc.base?.limitation_responsabilite ? ` (limitation ${fmtEur(calc.base.limitation_responsabilite)})` : ''}</span>
            <span>Franchise <b>{fmtEur(calc.franchise)}</b></span>
            <span>Nette <b>{fmtEur(calc.indemnite_nette)}</b></span>
            <span className="text-slate-400">Regle : {calc.regle?.source}{calc.regle?.pourcentage ? ` · ${calc.regle.pourcentage}%` : ''}{calc.regle?.plafond ? ` · plafond ${fmtEur(calc.regle.plafond)}` : ''} · calcule le {fmtDate(calc.date_calcul)}</span>
            {calc.regle?.alerte && <span className="text-amber-600 flex items-center gap-1"><AlertTriangle size={11} /> {calc.regle.alerte}</span>}
          </div>
        )}
      </div>
      {open && (
        <form onSubmit={submit} className="border-t border-slate-100 bg-sky-50/40 p-5 grid grid-cols-2 gap-3 text-sm" data-testid="form-qualifier">
          <div>
            <label className="text-xs text-slate-500">Qualification</label>
            <select value={form.qualification} onChange={e => setForm({ ...form, qualification: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg bg-white" data-testid="select-qualification">
              {Object.entries(QUALIF_LABELS).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-xs text-slate-500">Entite</label>
            <select value={form.entite_id} onChange={e => setForm({ ...form, entite_id: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg bg-white" data-testid="select-entite">
              <option value="">— inchangee —</option>
              {entites.map(e => <option key={e.id} value={e.id}>{e.nom}</option>)}
            </select>
          </div>
          {data?.garanties?.length > 0 && (
            <div>
              <label className="text-xs text-slate-500">Garantie mobilisee</label>
              <select value={form.garantie_id} onChange={e => setForm({ ...form, garantie_id: e.target.value })} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg bg-white" data-testid="select-garantie">
                <option value="">— aucune —</option>
                {data.garanties.map(g => <option key={g.id} value={g.id}>{`${g.libelle}${g.franchise ? ` (franchise ${fmtEur(g.franchise)})` : ''}`}</option>)}
              </select>
            </div>
          )}
          <div className="col-span-2">
            <label className="text-xs text-slate-500">Motif (obligatoire)</label>
            <textarea value={form.motif} onChange={e => setForm({ ...form, motif: e.target.value })} rows={2} className="w-full px-2 py-1.5 border border-slate-200 rounded-lg bg-white" placeholder="Pourquoi cette qualification ?" data-testid="input-motif" />
          </div>
          <div className="col-span-2 flex justify-end gap-2">
            <button type="button" onClick={() => setOpen(false)} className="px-3 py-1.5 text-xs rounded-lg border border-slate-200">Annuler</button>
            <button type="submit" disabled={saving} className="px-3 py-1.5 text-xs rounded-lg bg-sky-600 text-white disabled:opacity-50" data-testid="submit-qualifier">{saving ? 'Enregistrement…' : 'Valider la qualification'}</button>
          </div>
        </form>
      )}
    </div>
  )
}
